import type { SetlistContext } from './client';

import { CONTRIBUTORS_COLLECTION } from './collections';
import type { ContributorDocument } from './contributor-documents';
import { escapeTypesenseContributorName } from './contributor-filters';

import { MAX_ROWS_PER_CONTRIBUTOR } from './contributor-type-mapping';

// Batched variant of `getContributorSlugByName`: one Typesense query against
// `contributors.v1` resolves every name in the input list. Returns a
// `name → slug` map covering only the names that actually matched a row with
// a non-empty slug. Used by SFX surfaces (and any future asset type whose
// docs don't ship `contributor_slug`) so listing pages don't fan out one
// query per visible contributor.
export const getContributorSlugsByNames = async (
  context: SetlistContext,
  names: readonly string[]
): Promise<Record<string, string>> => {
  const uniqueNames = Array.from(
    new Set(
      names
        .map((name) => escapeTypesenseContributorName(name))
        .filter((name) => name.length > 0)
    )
  );

  if (uniqueNames.length === 0) {
    return {};
  }

  // contributors.v1 stores one row per asset type, so a multi-asset
  // contributor can return several rows for the same name. Overshoot the
  // per_page cap to make sure we collect every row before picking a slug.
  //
  // The 250 ceiling is a soft guard against unbounded Typesense round-trip
  // cost — well above today's listing-page fan-out (~20-30 contributors per
  // SFX page × 6 rows = ~180). If a future caller pushes near this ceiling
  // the silent row truncation would mask missing slugs, so revisit the cap
  // (or paginate) before then.
  const SOFT_PER_PAGE_CEILING = 250;
  const requestedPerPage = uniqueNames.length * MAX_ROWS_PER_CONTRIBUTOR;
  const perPage = Math.min(requestedPerPage, SOFT_PER_PAGE_CEILING);

  // Report when we clip a request so we hear about
  // the truncation before it starts hiding slugs in prod.
  if (requestedPerPage > SOFT_PER_PAGE_CEILING) {
    context.onError(
      new Error('getContributorSlugsByNames hit SOFT_PER_PAGE_CEILING'),
      {
        operation: 'getContributorSlugsByNames',
        nameCount: uniqueNames.length,
        requestedPerPage,
        ceiling: SOFT_PER_PAGE_CEILING,
      }
    );
  }

  const filterValue = uniqueNames.map((name) => `\`${name}\``).join(',');

  try {
    const response = await context.client
      .collections<ContributorDocument>(CONTRIBUTORS_COLLECTION)
      .documents()
      .search(
        {
          q: '*',
          query_by: 'name',
          filter_by: `name:=[${filterValue}]`,
          per_page: perPage,
          include_fields: 'name,slug',
        },
        { cacheSearchResultsForSeconds: 300 }
      );

    const map: Record<string, string> = {};
    for (const hit of response.hits ?? []) {
      const name = hit.document?.name?.trim();
      const slug = hit.document?.slug?.trim();
      if (!(name && slug)) {
        continue;
      }
      // Some per-asset-type rows store an empty slug — keep the first
      // populated slug we see for each name.
      if (!map[name]) {
        map[name] = slug;
      }
    }
    return map;
  } catch (error) {
    context.onError(error, {
      operation: 'getContributorSlugsByNames',
      nameCount: uniqueNames.length,
    });
    return {};
  }
};
